import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import axios from "axios";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Instagram,
  Facebook,
  Globe,
  QrCode,
  ArrowUpRight,
  Loader2,
  Check,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { profile } from "@/data/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const socialIcon = (label) => {
  const l = label.toLowerCase();
  if (l.includes("linked")) return Linkedin;
  if (l.includes("instagram")) return Instagram;
  if (l.includes("facebook")) return Facebook;
  return Globe;
};

const Label = ({ children }) => (
  <span className="text-xs tracking-[0.25em] uppercase text-gold">{children}</span>
);

const Card = ({ children, className = "", ...props }) => (
  <motion.div
    variants={fadeUp}
    whileHover={{ y: -4 }}
    transition={{ type: "spring", stiffness: 200, damping: 20 }}
    className={`bg-forest-800/40 backdrop-blur-2xl border border-gold/15 shadow-2xl rounded-sm ${className}`}
    {...props}
  >
    {children}
  </motion.div>
);

export default function ContactPage() {
  const [siteUrl, setSiteUrl] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [news, setNews] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsDone, setNewsDone] = useState(false);

  useEffect(() => {
    setSiteUrl(window.location.href);
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — Joseph will be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onNewsletter = async (e) => {
    e.preventDefault();
    if (!news) {
      toast.error("Please enter your email.");
      return;
    }
    setNewsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: news });
      setNewsDone(true);
      toast.success("You're subscribed — welcome aboard.");
      setNews("");
      setTimeout(() => setNewsDone(false), 4000);
    } catch (err) {
      toast.error("Please enter a valid email and try again.");
    } finally {
      setNewsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-forest text-white">
      <Toaster theme="dark" position="top-center" richColors />

      {/* Ambient background layers */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.12] mix-blend-overlay"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1629197520635-16570fbd0bb3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -top-40 -left-40 h-[38rem] w-[38rem] rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.10), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -bottom-56 -right-40 h-[42rem] w-[42rem] rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(13,51,39,0.7), transparent 70%)" }}
      />

      <motion.main
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.06 } } }}
        className="relative z-10 mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24 lg:py-28"
      >
        {/* Top bar */}
        <motion.header
          variants={fadeUp}
          className="mb-12 flex items-center justify-between md:mb-16"
        >
          <span className="font-display text-xl tracking-tight text-white" data-testid="brand-mark">
            {profile.company}
          </span>
          <Label>Private Contact</Label>
        </motion.header>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-5 md:gap-6">
          {/* Hero / Profile */}
          <Card className="col-span-12 p-8 md:p-12 lg:col-span-8" data-testid="profile-card">
            <div className="flex flex-col items-start gap-8 md:flex-row md:gap-12">
              <div className="relative shrink-0">
                <div className="absolute -inset-2 rounded-sm border border-gold/25" aria-hidden />
                <img
                  src={profile.portrait}
                  alt={profile.name}
                  data-testid="profile-portrait"
                  className="relative h-56 w-44 rounded-sm object-cover grayscale-[15%] md:h-72 md:w-56"
                  style={{ objectPosition: "62% 35%" }}
                />
              </div>
              <div className="flex-1">
                <Label>{profile.tagline}</Label>
                <h1
                  className="font-display mt-4 text-5xl font-light leading-[1.05] tracking-tight text-white sm:text-6xl"
                  data-testid="profile-name"
                >
                  {profile.name}
                </h1>
                <p className="mt-4 text-lg text-gold">{profile.title}</p>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
                  {profile.bio}
                </p>
              </div>
            </div>
          </Card>

          {/* Scan & Connect */}
          <Card
            className="col-span-12 flex flex-col p-8 md:p-10 lg:col-span-4"
            data-testid="scan-card"
          >
            <div className="mb-6 flex items-center gap-2">
              <QrCode className="h-4 w-4 text-gold" />
              <Label>Scan to Connect</Label>
            </div>

            <div className="mx-auto flex w-full max-w-[220px] flex-col items-center rounded-sm border border-gold bg-white p-5 shadow-[0_0_40px_rgba(212,175,55,0.12)]">
              {siteUrl ? (
                <QRCode
                  value={siteUrl}
                  size={180}
                  fgColor="#04140F"
                  bgColor="#ffffff"
                  data-testid="qr-code"
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              ) : (
                <div className="h-[180px] w-[180px] animate-pulse bg-forest/10" />
              )}
            </div>
            <p className="mt-4 text-center text-xs text-white/50">
              Point your camera here to open this page.
            </p>

            <div className="mt-8 border-t border-gold/15 pt-6">
              <Label>Elsewhere</Label>
              <div className="mt-4 flex flex-col gap-3">
                {profile.socials.map((s) => {
                  const Icon = socialIcon(s.label);
                  const tid = `social-${s.label.replace(/[^a-z]/gi, "").toLowerCase()}`;
                  if (!s.url) {
                    return (
                      <div
                        key={s.label}
                        data-testid={tid}
                        className="flex items-center gap-3 px-1 py-1.5 text-white/50"
                      >
                        <Icon className="h-5 w-5 text-gold/60" />
                        <span className="text-sm italic">{s.handle}</span>
                      </div>
                    );
                  }
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={tid}
                      className="group flex items-center justify-between rounded-sm px-1 py-1.5 text-white/80 transition-colors hover:text-gold-light"
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-gold transition-colors group-hover:text-gold-light" />
                        <span className="text-sm">{s.handle}</span>
                      </span>
                      <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                    </a>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Contact details */}
          <Card
            className="col-span-12 p-8 md:col-span-5 md:p-10 lg:col-span-4"
            data-testid="details-card"
          >
            <Label>Direct Lines</Label>
            <div className="mt-6 flex flex-col gap-6">
              {[
                { Icon: Mail, text: profile.contact.email, href: `mailto:${profile.contact.email}`, tid: "contact-email" },
                { Icon: Phone, text: profile.contact.phone, href: `tel:${profile.contact.phone.replace(/[^+\d]/g, "")}`, tid: "contact-phone" },
                { Icon: MapPin, text: profile.contact.location, href: null, tid: "contact-location" },
              ].map(({ Icon, text, href, tid }) => {
                const inner = (
                  <>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-gold/25 bg-forest/40">
                      <Icon className="h-5 w-5 text-gold" />
                    </span>
                    <span className="text-base text-white/85">{text}</span>
                  </>
                );
                return href ? (
                  <a
                    key={tid}
                    href={href}
                    data-testid={tid}
                    className="flex items-center gap-4 transition-colors hover:text-gold-light"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={tid} data-testid={tid} className="flex items-center gap-4">
                    {inner}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Contact form */}
          <Card
            className="col-span-12 p-8 md:col-span-7 md:p-12 lg:col-span-8"
            data-testid="form-card"
          >
            <Label>Start a Conversation</Label>
            <h2 className="font-display mt-3 text-3xl font-normal tracking-tight text-white sm:text-4xl">
              Let's talk.
            </h2>

            <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your name"
                  data-testid="form-name-input"
                  className="mt-2 w-full rounded-none border-b border-gold/30 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-white/30 focus:border-gold"
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                  data-testid="form-email-input"
                  className="mt-2 w-full rounded-none border-b border-gold/30 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-white/30 focus:border-gold"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell me a little about your interest..."
                  data-testid="form-message-input"
                  className="mt-2 w-full resize-none rounded-none border-b border-gold/30 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-white/30 focus:border-gold"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-button"
                  className="group inline-flex items-center gap-3 border border-gold px-8 py-4 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold hover:text-forest disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : sent ? (
                    <>
                      <Check className="h-4 w-4" /> Sent
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Card>
        </div>

        {/* Newsletter bar */}
        <motion.div
          variants={fadeUp}
          className="mt-6 rounded-sm border border-gold/15 bg-forest-800/40 p-8 backdrop-blur-2xl shadow-2xl md:p-10"
          data-testid="newsletter-card"
        >
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <Label>Stay in the Loop</Label>
              <h2 className="font-display mt-3 text-2xl font-normal tracking-tight text-white sm:text-3xl">
                Join the newsletter.
              </h2>
              <p className="mt-2 max-w-md text-sm text-white/60">
                Occasional updates on Hithlain Technologies — new capabilities, milestones, and field notes.
              </p>
            </div>
            <form
              onSubmit={onNewsletter}
              className="flex w-full max-w-md items-center gap-3 md:w-auto"
            >
              <input
                type="email"
                name="newsletter-email"
                value={news}
                onChange={(e) => setNews(e.target.value)}
                placeholder="you@email.com"
                data-testid="newsletter-email-input"
                className="w-full rounded-none border-b border-gold/30 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-white/30 focus:border-gold md:w-64"
              />
              <button
                type="submit"
                disabled={newsLoading}
                data-testid="newsletter-submit-button"
                className="group inline-flex shrink-0 items-center gap-2 border border-gold px-6 py-3 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold hover:text-forest disabled:cursor-not-allowed disabled:opacity-60"
              >
                {newsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : newsDone ? (
                  <>
                    <Check className="h-4 w-4" /> Done
                  </>
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.footer
          variants={fadeUp}
          className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-gold/10 pt-8 text-sm text-white/40 sm:flex-row"
        >
          <span>
            © {new Date().getFullYear()} {profile.name}. All rights reserved.
          </span>
          <span className="font-display italic text-white/50">{profile.tagline}</span>
        </motion.footer>
      </motion.main>
    </div>
  );
}
